import { CatalogType, Tone } from '@/constants/catalogManager'
import {
  DestinationCatalog,
  Fraction,
  ProductCatalogRequest
} from '@/types/DestinationCatalog'
import { ProductParmeter } from '@/types/productParamter'
import { AttributeValue } from '@/types/attributeValue'
import { defineStore } from 'pinia'
import { useAppStore } from './app'
import CatalogService from '@/services/catalogService'
import ProductParameterService from '@/services/productParamaterService'
import { ProductVariant } from '@/types/productVariants'
import ProductVariantService from '@/services/productVariantService'
import { EnrichmentResponse, EnrichmentRequest } from '@/types/enrichment'
import EnrichmentService from '@/services/enrichmentService'
import { useNotificationStore } from './notification'
import { useI18n } from 'vue-i18n'
import NotifyError from '@/utils/NotifyError'
import {
  DurationType,
  Operators,
  RuleOperator
} from '@/constants/productInsights'
import { ProductInsightFields } from '@/constants/productInsights'
import { productImageDetail } from '@/types/ProductImageDetails'
import ProductImageService from '@/services/productImageService'
import { ProductStatus } from '@/constants/ProductStatus'
import { ItemType } from '@/constants/productVariants'
import { cloneDeep } from 'lodash'

interface CatalogState {
  selectedCatalogData: DestinationCatalog | null
  sourceCatalogId: number | null
  productParameters: ProductParmeter[] | null
  attributeValues: AttributeValue[] | null
  fractionEnrichedProducts: Map<number, ProductVariant[]>
  productImageDetails: Map<number, Array<productImageDetail>>
  productVariantImageDetails: Map<number, Array<productImageDetail>>
  // Base products fetched once for live preview, keyed by fractionIndex
  previewBaseProducts: Map<number, ProductVariant[]>
}

export const useCatalogStore = defineStore('catalog', {
  state: (): CatalogState => ({
    selectedCatalogData: null,
    sourceCatalogId: null,
    productParameters: null,
    attributeValues: null,
    fractionEnrichedProducts: new Map<number, ProductVariant[]>(),
    productImageDetails: new Map<number, productImageDetail[]>(),
    productVariantImageDetails: new Map<number, productImageDetail[]>(),
    previewBaseProducts: new Map<number, ProductVariant[]>()
  }),
  getters: {
    getSelectedCatalogData(): DestinationCatalog | null {
      return this.selectedCatalogData
    },
    getSourceCatalogId(): number | null {
      return this.sourceCatalogId
    },
    getProductParameters(): ProductParmeter[] | null {
      return this.productParameters
    },
    getAttributeValues(): AttributeValue[] | null {
      return this.attributeValues
    },
    getFractionEnrichmentData:
      state =>
      (fractionIndex: number): ProductVariant[] | null => {
        return state.fractionEnrichedProducts.get(fractionIndex) || null
      },
    getProductImageDetails:
      state =>
      (productId: number): productImageDetail[] | null => {
        return state.productImageDetails.get(productId) || null
      },
    getProductVariantImageDetails:
      state =>
      (productVariantId: number): productImageDetail[] | null => {
        return state.productVariantImageDetails.get(productVariantId) || null
      },
    getPreviewBaseProducts:
      state =>
      (fractionIndex: number): ProductVariant[] | null => {
        return state.previewBaseProducts.get(fractionIndex) || null
      }
  },
  actions: {
    setSelectedCatalogData(catalogData: DestinationCatalog | null): void {
      this.selectedCatalogData = catalogData
    },
    setSourceCatalogId(catalogId: number) {
      this.sourceCatalogId = catalogId
    },
    setFractionEnrichedProducts(fractionIndex: number, data: ProductVariant[]) {
      this.fractionEnrichedProducts.set(fractionIndex, data)
    },
    async fetchAndSetSourceCatalogId() {
      const appStore = useAppStore()
      const catalogService = new CatalogService()
      const productCatalogFilter: ProductCatalogRequest = {
        type: CatalogType.Source
      }
      const catalogResponse = await catalogService.getPaginatedCatalogs(
        appStore.getSelectedBusinessProfileId,
        productCatalogFilter
      )
      if (
        (catalogResponse?.data?.data?.length ?? 0) > 0 &&
        catalogResponse?.data?.data[0]?.id
      ) {
        this.setSourceCatalogId(catalogResponse.data.data[0].id)
      }
    },
    async fetchAndSetProductParameters(businessProfileId: number) {
      const productParamaterService = new ProductParameterService()
      const productParameters: ProductParmeter[] =
        await productParamaterService.getProductParameters(businessProfileId)
      this.productParameters = productParameters
    },
    async fetchAndSetAttributeValues(
      productParameterIds: string,
      businessProfileId: number,
      catalogId: number
    ) {
      const catalogService = new CatalogService()
      const attributeValues: AttributeValue[] =
        await catalogService.getAttributeValues(
          productParameterIds,
          businessProfileId,
          catalogId
        )
      this.attributeValues = attributeValues
    },
    async fetchAndSetFractionEnrichmentDataAsync(
      fractionIndex: number,
      fraction: Fraction,
      catalogId: number,
      pageNumber: number = 1,
      pageSize: number = 5,
      productsToEnrich?: ProductVariant[]
    ) {
      if (!this.getSourceCatalogId) {
        await this.fetchAndSetSourceCatalogId()
      }
      const sourceCatalogId = this.getSourceCatalogId
      const productVariantService = new ProductVariantService()
      const appStore = useAppStore()
      const enrichmentService = new EnrichmentService()

      if (sourceCatalogId) {
        let data: ProductVariant[] = []

        if (productsToEnrich && productsToEnrich.length > 0) {
          data = cloneDeep(productsToEnrich)
        } else {
          const filterRuleJson = {
            groups: [
              {
                operator: RuleOperator.AND,
                filterType: 1,
                rules: [
                  {
                    operator: RuleOperator.AND,
                    durationType: DurationType.FIXED,
                    filters: [
                      {
                        field: ProductInsightFields.ProductVariantDescription,
                        operator: Operators.IsNotNull,
                        value: ''
                      },
                      {
                        field: ProductInsightFields.ProductVariantStatus,
                        operator: Operators.Equals,
                        value: '1'
                      }
                    ]
                  }
                ]
              }
            ]
          }

          const response = await productVariantService.getProductVariant(
            appStore.getSelectedBusinessProfileId,
            sourceCatalogId,
            {
              pageNumber: pageNumber,
              pageSize: pageSize,
              statusFilter: [
                ProductStatus.Active,
                ProductStatus.Restricted,
                ProductStatus.Excluded
              ],
              itemTypeFilter: [ItemType.Product, ItemType.Variant],
              ruleJson: JSON.stringify(filterRuleJson)
            }
          )
          data = (response?.data || []).filter(
            v =>
              v.title &&
              v.title.trim() !== '' &&
              v.description &&
              v.description.trim() !== ''
          )
        }

        if (data.length > 0) {
          data.forEach(item => {
            item.isEnriching = true
            item.enrichedTitle = null
            item.enrichedDescription = null
          })

          let productVariants: ProductVariant[] = data
          const { title, description } = fraction.enrichmentConfig
          const productIds = data.map(variant => variant.id).join(',')

          try {
            let enrichedData: EnrichmentResponse[] = []

            enrichedData = await enrichmentService.GetEnrichedData(
              appStore.getSelectedBusinessProfileId,
              {
                catalogId: catalogId,
                productIds,
                templateId: title.templateId,
                titleFormat: title.format,
                titleKeywords: [],
                descriptionLength:
                  description.contentLength === 0
                    ? 1
                    : description.contentLength,
                descriptionTone: description.enrichmentTone.tone ?? Tone.None,
                descriptionSeason: description.enrichmentTone.season,
                descriptionFestival: description.enrichmentTone.festival,
                descriptionPrompt: description.prompt
              }
            )

            data.forEach(item => {
              const enrichedItem = enrichedData.find(en => en.id === item.id)
              if (enrichedItem) {
                item.enrichedTitle = enrichedItem.formattedTitle
                item.enrichedDescription = enrichedItem.enrichedDescription
              }
              item.isEnriching = false
            })

            productVariants = [...data]
            this.fractionEnrichedProducts.set(fractionIndex, productVariants)
          } catch (error) {
            const notificationStore = useNotificationStore()
            const { t } = useI18n()
            notificationStore.notify(t('errors.somethingWentWrong'), 'error')
            NotifyError(error as Error)
            data.forEach(item => (item.isEnriching = false))
            throw error
          }
        }
      }
    },
    resetFractionEnrichmentData() {
      this.fractionEnrichedProducts = new Map<number, ProductVariant[]>()
    },
    async fetchAndSetProductImageDetails(
      businessProfileId: number,
      catalogId: number,
      productId: number
    ) {
      const productImageService = new ProductImageService()
      const productImageDetails =
        await productImageService.getProductImageDetails(
          businessProfileId,
          catalogId,
          productId
        )
      this.productImageDetails.set(productId, productImageDetails)
    },
    async fetchAndSetProductVariantImageDetails(
      businessProfileId: number,
      catalogId: number,
      productVariantId: number
    ) {
      const productImageService = new ProductImageService()
      const productImageDetails =
        await productImageService.getProductVariantImageDetails(
          businessProfileId,
          catalogId,
          productVariantId
        )
      this.productVariantImageDetails.set(productVariantId, productImageDetails)
    },
    setPreviewBaseProducts(fractionIndex: number, products: ProductVariant[]) {
      this.previewBaseProducts.set(fractionIndex, products)
    },
    clearPreviewBaseProducts(fractionIndex?: number) {
      if (fractionIndex !== undefined) {
        this.previewBaseProducts.delete(fractionIndex)
      } else {
        this.previewBaseProducts.clear()
      }
    }
  }
})
